/*
 * Copyright (c) 2024. Dave van Rijn Development
 */

import './style/passwordInput.scss';

import React, {KeyboardEventHandler, PureComponent} from 'react';

import {ControlVariant, ElementPosition, OrnamentShape, StrengthGauge} from "../../util/interfaces";
import TextField from '../v2/inputController_v2';
import AwesomeIcon from '../../icon/awesomeIcon';
import {directTimeout, generateComponentId} from "../../util/componentUtil";
import classNames from 'classnames';

interface Props {
    onChange: any;
    onFocus?: any;
    onBlur?: any;
    onClick?: any;
    onKeyDown?: any;
    onMouseEnter?: any;
    onMouseLeave?: any;
    onEnter?: KeyboardEventHandler;
    value: string;
    error?: string | null;
    label?: string;
    containerClass?: string;
    inputContainerClass?: string;
    inputClass?: string;
    labelClass?: string;
    errorClass?: string;
    id?: string;
    key?: string;
    baseColor?: string;
    variant?: ControlVariant;
    disabled?: boolean;
    readOnly?: boolean;
    fullWidth?: boolean;
    autoFocus?: boolean;
    inputProps?: object;
    autoSelect?: boolean;
    strengthGauche?: StrengthGauge[];
    strengthColors: string[];
    strengthLabels: string[];
    placeholder?: string;
}

interface State {
    type: 'password' | 'text';
    passwordStrength: number;
}

export default class PasswordInput extends PureComponent<Props, State> {
    private id = generateComponentId(this.props.id);
    private meter: HTMLDivElement;
    static defaultProps = {
        strengthColors: ['red', 'gold', '#29A71A'],
        strengthLabels: ['Zwak', 'Matig', 'Sterk'],
        area: false,
        clearAble: true,
        disabled: false,
        autoFocus: false,
        autoSelect: false,
        error: null,
        errorClassName: '',
        fullWidth: true,
        className: '',
        inputContainerClassName: '',
        labelClassName: '',
        readOnly: false,
        type: 'text',
        variant: ControlVariant.SIMPLE,
    };

    state: State = {
        type: 'password' as 'password',
        passwordStrength: 0,
    };

    onClickOrnament = () => {
        const {type} = this.state;
        if (type === 'password') this.setState({type: 'text'});
        else this.setState({type: 'password'});
    };

    getOrnament = (): OrnamentShape => {
        const {type} = this.state, element = type === 'password' ? 'eye' : 'eye-slash';
        return {
            placement: ElementPosition.RIGHT,
            element: <AwesomeIcon name={element} onClick={this.onClickOrnament} className='passwordOrnament'/>
        };
    };

    getGaucheWidth = (): { width: string, label: string } => {
        const {strengthGauche, value, strengthLabels} = this.props;
        if (!strengthGauche?.length) return {width: '0%', label: strengthLabels[0]};
        const total = strengthGauche.length;
        let passed: number = 0;
        for (let i = 0; i < strengthGauche.length; i++)
            if (strengthGauche[i].validator(value)) passed++;
        const percentage = Math.min(parseInt((passed / total * 100) + ''), 100),
            width = Math.max(percentage, Math.round(100 / total)) + '%';

        return {width, label: strengthLabels[Math.max(0, Math.min(strengthLabels.length - 1, passed - 1))]};
    };

    setGaucheWidth = () => {
        const input = document.getElementById(this.id);
        if (input && this.meter) this.meter.style.width = input.getBoundingClientRect().width + 'px';
    }

    renderStrengthGauche = () => {
        const {strengthGauche, strengthColors, value} = this.props;
        const hidden = !strengthGauche?.length || !value.length;
        const gaucheVars: { width: string, label: string } = this.getGaucheWidth();
        return (
            <div className={classNames('strength-gauche', hidden && 'hidden')} style={{width: gaucheVars.width}}>
                <div className='meter' ref={(ref: HTMLDivElement) => {this.meter = ref}}
                     style={{backgroundImage: `linear-gradient(to right, ${strengthColors.join(', ')})`}}/>
                <label className='gauche-label'>{gaucheVars.label}</label>
            </div>
        );
    }

    componentDidMount = () => {
        directTimeout(this.setGaucheWidth)
    };

    render = () => {
        const {type} = this.state, {inputProps} = this.props;
        return (
            <div className='dvr-password-field-container'>
                <TextField {...this.props} inputProps={{...inputProps, type}} id={this.id} unControlled>
                    {this.getOrnament()}
                </TextField>
                {this.renderStrengthGauche()}
            </div>
        );
    };
}